<script setup>
import { computed, useAttrs } from 'vue';

import { useTheme } from '../composables/useTheme';

/**
 * M3 toggle switch with optional icons, label positioning, and indeterminate state.
 *
 * @status internal
 * @group forms-input
 * @see https://quasar.dev/vue-components/toggle Quasar q-toggle API
 */
defineOptions({ name: 'HrSwitch' });

const props = defineProps({
  /**
   * Current value of the switch; compared against `trueValue`/`falseValue`
   * to determine on/off state. Use with v-model.
   */
  modelValue: {
    type: null,
    default: false,
  },
  /**
   * Value emitted when the switch is on.
   */
  trueValue: {
    type: null,
    default: true,
  },
  /**
   * Value emitted when the switch is off.
   */
  falseValue: {
    type: null,
    default: false,
  },
  /**
   * Value emitted when the switch is in an indeterminate state.
   */
  indeterminateValue: {
    type: null,
    default: null,
  },
  /** Label text shown beside the switch. */
  label: {
    type: String,
    default: null,
  },
  /**
   * Position of the label relative to the switch thumb.
   * @values left, right
   */
  labelPosition: {
    type: String,
    default: 'right',
    validator: (v) => ['left', 'right'].includes(v),
  },
  /**
   * Icon shown when the switch is on (checked-icon).
   */
  iconOn: {
    type: String,
    default: null,
  },
  /**
   * Icon shown when the switch is off (unchecked-icon).
   */
  iconOff: {
    type: String,
    default: null,
  },
  /** Prevents interaction and dims the switch. */
  disable: {
    type: Boolean,
    default: false,
  },
  /**
   * Enables cycling through an indeterminate state between on and off.
   */
  toggleIndeterminate: {
    type: Boolean,
    default: false,
  },
  /**
   * Legacy props forwarded to the underlying Quasar component.
   * @deprecated Only for backwards compatibility with the legacy theme — ignored when a design system theme is active.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits({
  /** Emitted when the switch is toggled; payload is the new value (`trueValue` or `falseValue`). */
  'update:modelValue': null,
});

const attrs = useAttrs();
const { hasTheme } = useTheme();

const classes = computed(() => ['hr-switch']);

const effectiveProps = computed(() => {
  const base = {
    modelValue: props.modelValue,
    trueValue: props.trueValue,
    falseValue: props.falseValue,
    indeterminateValue: props.indeterminateValue,
    toggleIndeterminate: props.toggleIndeterminate,
    disable: props.disable,
    ...(props.label !== null && { label: props.label }),
    leftLabel: props.labelPosition === 'left',
    ...(props.iconOn !== null && { checkedIcon: props.iconOn }),
    ...(props.iconOff !== null && { uncheckedIcon: props.iconOff }),
  };

  if (hasTheme.value) {
    return { ...base, ...attrs };
  }
  return { ...base, ...props.legacyProps, ...attrs };
});
</script>
<template>
  <q-toggle
    v-bind="effectiveProps"
    :class="hasTheme ? classes : undefined"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <template
      v-if="$slots.default"
      #default
    >
      <!-- @slot default Custom label content; use instead of the `label` prop for rich content. -->
      <slot />
    </template>
  </q-toggle>
</template>
<style lang="scss" scoped>

/* Only applies to light/dark themes */

/* M3 canonical switch dimensions — 52×32 dp track */
.hr-switch {
  --hr-sw-track-w:      52px;
  --hr-sw-track-h:      32px;
  --hr-sw-handle-off:   16px;
  --hr-sw-handle-on:    24px;
  --hr-sw-handle-press: 28px;
}

/* ── Inner container ─────────────────────────────────────────────── */
/*
 * font-size: 1px neutralises Quasar's em-based proportional sizing so we
 * can drive all dimensions from explicit px custom properties instead.
 * The label is a sibling (not a child) of the inner, so it is unaffected.
 */
.hr-switch :deep(.q-toggle__inner) {
  font-size: 1px;
  width: var(--hr-sw-track-w);
  min-width: var(--hr-sw-track-w);
  height: var(--app-comp-touch-target-size);
  /* vertical padding centers the track inside the touch target */
  padding: calc((var(--app-comp-touch-target-size) - var(--hr-sw-track-h)) / 2) 0;
  color: var(--app-on-surface-variant); /* currentColor used by state layer */
}

/* ── Track ───────────────────────────────────────────────────────── */
.hr-switch :deep(.q-toggle__track) {
  height: var(--hr-sw-track-h);
  border-radius: calc(var(--hr-sw-track-h) / 2);
  opacity: 1;
  background: var(--app-surface-container-highest);
  border: 2px solid var(--app-outline);
  box-sizing: border-box;
  transition: background 0.22s cubic-bezier(0.4, 0, 0.2, 1),
              border-color 0.22s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Override Quasar's hardcoded var(--q-primary) on the truthy inner */
.hr-switch :deep(.q-toggle__inner--truthy) {
  color: var(--app-primary); /* currentColor for state layer */
}

.hr-switch :deep(.q-toggle__inner--truthy .q-toggle__track) {
  background: var(--app-primary);
  border-color: transparent;
}

/* ── Thumb container (sized to handle-press so the state layer scales correctly) */
.hr-switch :deep(.q-toggle__thumb) {
  width: var(--hr-sw-handle-press);
  height: var(--hr-sw-handle-press);
  top: calc((var(--app-comp-touch-target-size) - var(--hr-sw-handle-press)) / 2);
  /* falsy: handle center at track-h/2 from track left */
  left: calc((var(--hr-sw-track-h) - var(--hr-sw-handle-press)) / 2);
  transition: left 0.22s cubic-bezier(0.4, 0, 0.2, 1);
}

.hr-switch :deep(.q-toggle__inner--indet .q-toggle__thumb) {
  left: calc((var(--hr-sw-track-w) - var(--hr-sw-handle-press)) / 2);
}

.hr-switch :deep(.q-toggle__inner--truthy .q-toggle__thumb) {
  /* truthy: handle center at track-w − track-h/2 from track left */
  left: calc(var(--hr-sw-track-w) - (var(--hr-sw-track-h) + var(--hr-sw-handle-press)) / 2);
}

/* ── Handle visual circle (:after) ───────────────────────────────── */
/*
 * The thumb container is always handle-press sized. The visual circle is
 * inset within it so it appears at the correct resting size, then grows
 * to fill the container on interaction.
 */
.hr-switch :deep(.q-toggle__thumb:after) {
  background: var(--app-outline);
  box-shadow: none;
  top:    calc((var(--hr-sw-handle-press) - var(--hr-sw-handle-off)) / 2);
  right:  calc((var(--hr-sw-handle-press) - var(--hr-sw-handle-off)) / 2);
  bottom: calc((var(--hr-sw-handle-press) - var(--hr-sw-handle-off)) / 2);
  left:   calc((var(--hr-sw-handle-press) - var(--hr-sw-handle-off)) / 2);
  transition:
    top    0.2s cubic-bezier(0.4, 0, 0.2, 1),
    right  0.2s cubic-bezier(0.4, 0, 0.2, 1),
    bottom 0.2s cubic-bezier(0.4, 0, 0.2, 1),
    left   0.2s cubic-bezier(0.4, 0, 0.2, 1),
    background 0.2s ease;
}

.hr-switch :deep(.q-toggle__inner--truthy .q-toggle__thumb:after) {
  background: var(--app-on-primary);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3), 0 1px 3px 1px rgba(0, 0, 0, 0.15);
  top:    calc((var(--hr-sw-handle-press) - var(--hr-sw-handle-on)) / 2);
  right:  calc((var(--hr-sw-handle-press) - var(--hr-sw-handle-on)) / 2);
  bottom: calc((var(--hr-sw-handle-press) - var(--hr-sw-handle-on)) / 2);
  left:   calc((var(--hr-sw-handle-press) - var(--hr-sw-handle-on)) / 2);
}

/* ── Handle grows to press size on interaction ───────────────────── */
.hr-switch:not(.disabled):hover       :deep(.q-toggle__thumb:after),
.hr-switch:not(.disabled):focus-within :deep(.q-toggle__thumb:after),
.hr-switch:not(.disabled):active      :deep(.q-toggle__thumb:after) {
  top: 0; right: 0; bottom: 0; left: 0;
}

/* ── Handle color shifts on interaction ────────────────────────── */
.hr-switch:not(.disabled):hover        :deep(.q-toggle__inner--falsy .q-toggle__thumb:after),
.hr-switch:not(.disabled):focus-within :deep(.q-toggle__inner--falsy .q-toggle__thumb:after),
.hr-switch:not(.disabled):active       :deep(.q-toggle__inner--falsy .q-toggle__thumb:after) {
  background: var(--app-on-surface-variant);
}

.hr-switch:not(.disabled):hover        :deep(.q-toggle__inner--truthy .q-toggle__thumb:after),
.hr-switch:not(.disabled):focus-within :deep(.q-toggle__inner--truthy .q-toggle__thumb:after),
.hr-switch:not(.disabled):active       :deep(.q-toggle__inner--truthy .q-toggle__thumb:after) {
  background: var(--app-primary-container);
}

/* ── State layer ─────────────────────────────────────────────────── */
/*
 * Quasar's body.desktop selector controls both opacity (0.12) and the
 * hover/focus scale (2×). We need body.desktop in our selector too to
 * win on specificity without !important.
 *
 * M3 state layer diameter: 40dp.
 * Thumb container: 28px (handle-press). Scale = 40 ÷ 28 ≈ 1.429.
 */
/* stylelint-disable-next-line selector-max-type */
body.desktop .hr-switch:not(.disabled) :deep(.q-toggle__thumb:before) {
  opacity: 0.08;
}

/* stylelint-disable-next-line selector-max-type */
body.desktop .hr-switch:not(.disabled):hover  :deep(.q-toggle__thumb:before),
/* stylelint-disable-next-line selector-max-type */
body.desktop .hr-switch:not(.disabled):focus  :deep(.q-toggle__thumb:before) {
  transform: scale3d(1.429, 1.429, 1);
}

/* ── Icons ───────────────────────────────────────────────────────── */
.hr-switch :deep(.q-toggle__inner .q-icon) {
  font-size: calc(var(--hr-sw-handle-on) * 0.65);
  min-width: unset;
  z-index: 1;
}

/* selected icon: on-primary-container */
.hr-switch :deep(.q-toggle__inner--truthy .q-icon) {
  color: var(--app-on-primary-container);
  opacity: 1;
}

/* unselected icon: surface-container-highest — appears as a cutout from the handle */
.hr-switch :deep(.q-toggle__inner--falsy .q-icon) {
  color: var(--app-surface-container-highest);
  opacity: 1;
}

/* ── Label ───────────────────────────────────────────────────────── */
/*
 * In default Quasar the gap between inner and label comes from the inner's
 * right padding (0.3em). We zeroed horizontal padding, so restore it here.
 */
.hr-switch :deep(.q-toggle__label) {
  padding-left: 16px;
  color: var(--app-on-surface);
}

/* left-label (reverse) — gap moves to the right side of the label */
.hr-switch.reverse :deep(.q-toggle__label) {
  padding-left: 0;
  padding-right: 16px;
}

/* ── Disabled ────────────────────────────────────────────────────── */
/* Override Quasar's blanket opacity: 0.75 !important so we can apply
   per-element M3 disabled tokens instead. */
.hr-switch.disabled {
  opacity: 1 !important;
}

.hr-switch.disabled :deep(.q-toggle__track) {
  background: color-mix(in srgb, var(--app-surface-container-highest) 12%, transparent);
  border-color: color-mix(in srgb, var(--app-on-surface) 12%, transparent);
}

.hr-switch.disabled :deep(.q-toggle__inner--truthy .q-toggle__track) {
  background: color-mix(in srgb, var(--app-on-surface) 12%, transparent);
  border-color: transparent;
}

.hr-switch.disabled :deep(.q-toggle__thumb:after) {
  background: color-mix(in srgb, var(--app-on-surface) 38%, transparent);
  box-shadow: none;
}

.hr-switch.disabled :deep(.q-toggle__inner--truthy .q-toggle__thumb:after) {
  background: var(--app-surface);
}

.hr-switch.disabled :deep(.q-toggle__label) {
  color: color-mix(in srgb, var(--app-on-surface) 38%, transparent);
}

.hr-switch.disabled :deep(.q-toggle__inner .q-icon) {
  color: color-mix(in srgb, var(--app-on-surface) 38%, transparent) !important;
  opacity: 1;
}
</style>
